import React, { useEffect, useState } from 'react';
import { X, Loader2, Plus } from 'lucide-react';
import { SelectInput } from '../FormControls';

const INPUT_CLASS =
  'w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-slate-700 focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40';

const getOptionId = (item) => (item && (item._id ?? item.id ?? '')) || '';
const getOptionLabel = (item) => (item ? (item.name ?? item.title ?? item.code ?? 'Untitled') : '');

/**
 * Build display text for selected location based on level.
 */
const getLocationDisplayText = (level, selections) => {
  const parts = [];
  if (selections.country) parts.push(selections.country.name ?? selections.country.title ?? '');
  if (level !== 'countryLevel' && selections.zone) {
    parts.push(selections.zone.name ?? selections.zone.title ?? '');
  }
  if (['regionLevel', 'districtLevel', 'wardLevel', 'venueLevel', 'locationLevel'].includes(level) && selections.region) {
    parts.push(selections.region.name ?? selections.region.title ?? '');
  }
  if (['districtLevel', 'wardLevel', 'venueLevel', 'locationLevel'].includes(level) && selections.district) {
    parts.push(selections.district.name ?? selections.district.title ?? '');
  }
  if (['wardLevel', 'venueLevel', 'locationLevel'].includes(level) && selections.ward) {
    parts.push(selections.ward.name ?? selections.ward.title ?? '');
  }
  if (['venueLevel', 'locationLevel'].includes(level) && selections.venue) {
    parts.push(selections.venue.name ?? selections.venue.title ?? '');
  }
  return parts.filter(Boolean).join(' > ') || '';
};

/**
 * Dialog opened when user clicks Select on the location selector.
 * Selection order depends on level: country → zone → region → district → ward → venue.
 * Stops at the specified level.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function(): void} props.onClose
 * @param {function(Object): void} [props.onSelect] - Called with selection object when user confirms
 * @param {string} props.level - 'countryLevel' | 'zoneLevel' | 'regionLevel' | 'districtLevel' | 'wardLevel' | 'venueLevel' | 'locationLevel'
 * @param {Array} [props.countries]
 * @param {Array} [props.zones]
 * @param {Array} [props.regions]
 * @param {Array} [props.districts]
 * @param {Array} [props.wards]
 * @param {Array} [props.venues]
 * @param {Object|null} props.selectedCountry
 * @param {Object|null} props.selectedZone
 * @param {Object|null} props.selectedRegion
 * @param {Object|null} props.selectedDistrict
 * @param {Object|null} props.selectedWard
 * @param {Object|null} props.selectedVenue
 * @param {function} props.setSelectedCountry
 * @param {function} props.setSelectedZone
 * @param {function} props.setSelectedRegion
 * @param {function} props.setSelectedDistrict
 * @param {function} props.setSelectedWard
 * @param {function} props.setSelectedVenue
 * @param {function} [props.resetSelections]
 * @param {boolean} [props.loading]
 * @param {function(Object): Promise<Object|null>} [props.onAddVenue] - When provided and level is venueLevel/locationLevel, allows adding a new venue. Called with { name, address?, ward, district?, region?, country? }; should return the created venue object.
 */
export function LocationSelectorDialog({
  isOpen,
  onClose,
  onSelect,
  level = 'venueLevel',
  countries = [],
  zones = [],
  regions = [],
  districts = [],
  wards = [],
  venues = [],
  selectedCountry,
  selectedZone,
  selectedRegion,
  selectedDistrict,
  selectedWard,
  selectedVenue,
  setSelectedCountry,
  setSelectedZone,
  setSelectedRegion,
  setSelectedDistrict,
  setSelectedWard,
  setSelectedVenue,
  applyVenueSelection,
  resetSelections,
  loading = false,
  onAddVenue,
}) {
  const [showAddVenueForm, setShowAddVenueForm] = useState(false);
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenueAddress, setNewVenueAddress] = useState('');
  const [addVenueLoading, setAddVenueLoading] = useState(false);
  const [addVenueError, setAddVenueError] = useState(null);

  useEffect(() => {
    if (isOpen && resetSelections) resetSelections();
  }, [isOpen, resetSelections]);

  useEffect(() => {
    if (!isOpen) {
      setShowAddVenueForm(false);
      setNewVenueName('');
      setNewVenueAddress('');
      setAddVenueError(null);
    }
  }, [isOpen]);

  const handleCountryChange = (e) => {
    const id = e.target.value;
    const obj = countries.find((c) => getOptionId(c) === id) ?? null;
    setSelectedCountry(obj);
  };

  const handleZoneChange = (e) => {
    const id = e.target.value;
    const obj = zones.find((z) => getOptionId(z) === id) ?? null;
    setSelectedZone(obj);
  };

  const handleRegionChange = (e) => {
    const id = e.target.value;
    const obj = regions.find((r) => getOptionId(r) === id) ?? null;
    setSelectedRegion(obj);
  };

  const handleDistrictChange = (e) => {
    const id = e.target.value;
    const obj = districts.find((d) => getOptionId(d) === id) ?? null;
    setSelectedDistrict(obj);
  };

  const handleWardChange = (e) => {
    const id = e.target.value;
    const obj = wards.find((w) => getOptionId(w) === id) ?? null;
    setSelectedWard(obj);
  };

  const handleVenueChange = (e) => {
    const id = e.target.value;
    const obj = venues.find((v) => getOptionId(v) === id) ?? null;
    if (obj && applyVenueSelection) {
      applyVenueSelection(obj);
      return;
    }
    setSelectedVenue(obj);
  };

  const handleAddVenue = async () => {
    const name = newVenueName?.trim();
    if (!name || !onAddVenue || !selectedWard) return;
    setAddVenueError(null);
    setAddVenueLoading(true);
    try {
      const payload = {
        name,
        address: newVenueAddress?.trim() || undefined,
        ward: getOptionId(selectedWard),
        district: getOptionId(selectedDistrict) || undefined,
        region: getOptionId(selectedRegion) || undefined,
        country: getOptionId(selectedCountry) || undefined,
      };
      const created = await onAddVenue(payload);
      if (created) {
        setSelectedVenue(created);
        setShowAddVenueForm(false);
        setNewVenueName('');
        setNewVenueAddress('');
      }
    } catch (err) {
      setAddVenueError(err?.message ?? 'Failed to add venue. Please try again.');
    } finally {
      setAddVenueLoading(false);
    }
  };

  const handleConfirm = () => {
    const selections = {
      country: selectedCountry,
      zone: selectedZone,
      region: selectedRegion,
      district: selectedDistrict,
      ward: selectedWard,
      venue: selectedVenue,
      displayText: getLocationDisplayText(level, {
        country: selectedCountry,
        zone: selectedZone,
        region: selectedRegion,
        district: selectedDistrict,
        ward: selectedWard,
        venue: selectedVenue,
      }),
    };

    // Determine final selection based on level
    let finalSelection = null;
    if (level === 'countryLevel' && selectedCountry) {
      finalSelection = { ...selections, location: selectedCountry };
    } else if (level === 'zoneLevel' && selectedZone) {
      finalSelection = { ...selections, location: selectedZone };
    } else if (level === 'regionLevel' && selectedRegion) {
      finalSelection = { ...selections, location: selectedRegion };
    } else if (level === 'districtLevel' && selectedDistrict) {
      finalSelection = { ...selections, location: selectedDistrict };
    } else if (level === 'wardLevel' && selectedWard) {
      finalSelection = { ...selections, location: selectedWard };
    } else if (level === 'venueLevel' && selectedVenue) {
      finalSelection = { ...selections, location: selectedVenue };
    } else if (level === 'locationLevel') {
      const deepestSelection =
        selectedVenue ||
        selectedWard ||
        selectedDistrict ||
        selectedRegion ||
        selectedZone ||
        selectedCountry ||
        null;
      if (deepestSelection) {
        finalSelection = { ...selections, location: deepestSelection };
      }
    }

    if (finalSelection) {
      onSelect?.(finalSelection);
    }
  };

  if (!isOpen) return null;

  const canSelectZone = !!selectedCountry && level !== 'countryLevel';
  const canSelectRegion =
    !!selectedZone && ['regionLevel', 'districtLevel', 'wardLevel', 'venueLevel', 'locationLevel'].includes(level);
  const canSelectDistrict = !!selectedRegion && ['districtLevel', 'wardLevel', 'venueLevel', 'locationLevel'].includes(level);
  const canSelectWard = !!selectedDistrict && ['wardLevel', 'venueLevel', 'locationLevel'].includes(level);
  const canSelectVenue = !!selectedRegion && ['venueLevel', 'locationLevel'].includes(level);

  const canConfirm =
    !loading &&
    ((level === 'countryLevel' && selectedCountry) ||
      (level === 'zoneLevel' && selectedZone) ||
      (level === 'regionLevel' && selectedRegion) ||
      (level === 'districtLevel' && selectedDistrict) ||
      (level === 'wardLevel' && selectedWard) ||
      (level === 'venueLevel' && selectedVenue) ||
      (level === 'locationLevel' &&
        (selectedVenue || selectedWard || selectedDistrict || selectedRegion || selectedZone || selectedCountry)));

  const getLevelLabel = () => {
    switch (level) {
      case 'countryLevel':
        return 'Country';
      case 'zoneLevel':
        return 'Zone';
      case 'regionLevel':
        return 'Region';
      case 'districtLevel':
        return 'District';
      case 'wardLevel':
        return 'Ward';
      case 'venueLevel':
        return 'Venue';
      case 'locationLevel':
        return 'Location';
      default:
        return 'Location';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-dialog-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col relative">
        <div className="p-6 pb-2 flex-none">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded text-slate-500 hover:bg-gray-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 id="location-dialog-title" className="text-lg font-semibold text-primary-dark pr-8 mb-4">
            Select {getLevelLabel()}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 space-y-2 pb-4">
          {level === 'countryLevel' ||
          level === 'zoneLevel' ||
          level === 'regionLevel' ||
          level === 'districtLevel' ||
          level === 'wardLevel' ||
          level === 'venueLevel' ||
          level === 'locationLevel' ? (
            <SelectInput
              id="location-dialog-country"
              value={getOptionId(selectedCountry)}
              onChange={handleCountryChange}
              disabled={loading}
            >
              <option value="">Select country...</option>
              {countries.map((c) => (
                <option key={getOptionId(c)} value={getOptionId(c)}>
                  {getOptionLabel(c)}
                </option>
              ))}
            </SelectInput>
          ) : null}

          {(level === 'zoneLevel' ||
            level === 'regionLevel' ||
            level === 'districtLevel' ||
            level === 'wardLevel' ||
            level === 'venueLevel' ||
            level === 'locationLevel') && (
            <SelectInput
              id="location-dialog-zone"
              value={getOptionId(selectedZone)}
              onChange={handleZoneChange}
              disabled={loading || !canSelectZone}
            >
              <option value="">Select zone...</option>
              {zones.map((z) => (
                <option key={getOptionId(z)} value={getOptionId(z)}>
                  {getOptionLabel(z)}
                </option>
              ))}
            </SelectInput>
          )}

          {(level === 'regionLevel' ||
            level === 'districtLevel' ||
            level === 'wardLevel' ||
            level === 'venueLevel' ||
            level === 'locationLevel') && (
            <SelectInput
              id="location-dialog-region"
              value={getOptionId(selectedRegion)}
              onChange={handleRegionChange}
              disabled={loading || !canSelectRegion}
            >
              <option value="">Select region...</option>
              {regions.map((r) => (
                <option key={getOptionId(r)} value={getOptionId(r)}>
                  {getOptionLabel(r)}
                </option>
              ))}
            </SelectInput>
          )}

          {(level === 'districtLevel' || level === 'wardLevel' || level === 'venueLevel' || level === 'locationLevel') && (
            <SelectInput
              id="location-dialog-district"
              value={getOptionId(selectedDistrict)}
              onChange={handleDistrictChange}
              disabled={loading || !canSelectDistrict}
            >
              <option value="">Select district...</option>
              {districts.map((d) => (
                <option key={getOptionId(d)} value={getOptionId(d)}>
                  {getOptionLabel(d)}
                </option>
              ))}
            </SelectInput>
          )}

          {(level === 'wardLevel' || level === 'venueLevel' || level === 'locationLevel') && (
            <SelectInput
              id="location-dialog-ward"
              value={getOptionId(selectedWard)}
              onChange={handleWardChange}
              disabled={loading || !canSelectWard}
            >
              <option value="">Select ward...</option>
              {wards.map((w) => (
                <option key={getOptionId(w)} value={getOptionId(w)}>
                  {getOptionLabel(w)}
                </option>
              ))}
            </SelectInput>
          )}

          {['venueLevel', 'locationLevel'].includes(level) && (
            <SelectInput
              id="location-dialog-venue"
              value={getOptionId(selectedVenue)}
              onChange={handleVenueChange}
              disabled={loading || !canSelectVenue}
            >
              <option value="">Select venue...</option>
              {venues.map((v) => (
                <option key={getOptionId(v)} value={getOptionId(v)}>
                  {getOptionLabel(v)}
                </option>
              ))}
            </SelectInput>
          )}

          {['venueLevel', 'locationLevel'].includes(level) && canSelectVenue && !selectedVenue ? (
            <p className="text-xs text-slate-500">
              Pick a venue after selecting a region. District and ward are optional filters that can narrow the venue list first.
            </p>
          ) : null}

          {['venueLevel', 'locationLevel'].includes(level) && onAddVenue && selectedWard && (
            <div className="pt-2 mt-2 border-t border-gray-200 space-y-2">
              {!showAddVenueForm ? (
                <button
                  type="button"
                  onClick={() => setShowAddVenueForm(true)}
                  disabled={loading}
                  className="text-sm text-primary-dark hover:text-primary font-medium flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Can&apos;t find your venue? Add new
                </button>
              ) : (
                <div className="space-y-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <p className="text-xs font-medium text-slate-600">Add new venue</p>
                  <input
                    type="text"
                    value={newVenueName}
                    onChange={(e) => setNewVenueName(e.target.value)}
                    placeholder="Venue name"
                    disabled={addVenueLoading}
                    className={INPUT_CLASS}
                    aria-label="Venue name"
                  />
                  <input
                    type="text"
                    value={newVenueAddress}
                    onChange={(e) => setNewVenueAddress(e.target.value)}
                    placeholder="Address (optional)"
                    disabled={addVenueLoading}
                    className={INPUT_CLASS}
                    aria-label="Venue address"
                  />
                  {addVenueError && (
                    <p className="text-xs text-red-600">{addVenueError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleAddVenue}
                      disabled={addVenueLoading || !newVenueName?.trim()}
                      className="px-2 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded border border-primary focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {addVenueLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      Add venue
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddVenueForm(false);
                        setNewVenueName('');
                        setNewVenueAddress('');
                        setAddVenueError(null);
                      }}
                      disabled={addVenueLoading}
                      className="px-2 py-1.5 text-xs font-medium text-slate-700 bg-gray-200 hover:bg-gray-300 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {loading && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading...
            </p>
          )}
        </div>

        <div className="p-6 pt-2 flex justify-end gap-2 border-t border-gray-200 flex-none">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-dark/30"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded border border-primary focus:outline-none focus:ring-2 focus:ring-primary-dark/30 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
}

export default LocationSelectorDialog;
